// Shared helpers for @-mentions in textareas/inputs.
// Used by sticky-note (deal notes) and feed posts/comments.

import { useCallback, useState } from "react";

export function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function getFullName(u) {
  return `${u?.firstname || ""} ${u?.lastname || ""}`.trim();
}

// Scan text for `@First Last` substrings that match any user in `users`.
// Returns a Set of matched user ids.
export function extractMentionedIds(text, users) {
  const ids = new Set();
  (users || []).forEach((u) => {
    const name = getFullName(u);
    if (!name) return;
    const re = new RegExp(`@${escapeRegExp(name)}(?![\\w])`, "g");
    if (re.test(text || "")) ids.add(u.id);
  });
  return ids;
}

// A small React hook that manages mention autocomplete state for a textarea/
// input. Returns the props you spread onto the field plus the dropdown state
// and helpers.
//
// Usage:
//   const m = useMentionAutocomplete({ text, setText, users, currentUserId });
//   <textarea ref={m.fieldRef} value={text} {...m.fieldHandlers} />
//   {m.showMentions && <div>{m.candidates.map(u => ...onClick={() => m.selectMention(u)})}</div>}
export function useMentionAutocomplete({
  text,
  setText,
  users,
  currentUserId,
  fieldRef,
}) {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);

  const onChange = useCallback(
    (e) => {
      const value = e.target.value;
      setText(value);
      const cursorPos = e.target.selectionStart;
      setCursorPosition(cursorPos);

      const before = value.slice(0, cursorPos);
      const match = before.match(/@([\w\s]*)$/);
      if (match) {
        setMentionQuery(match[1]);
        setShowMentions(true);
      } else {
        setShowMentions(false);
      }
    },
    [setText],
  );

  const candidates = (users || [])
    .filter((u) => {
      const fullName = getFullName(u).toLowerCase();
      return (
        u?.id !== currentUserId &&
        u?.type !== 1 && // deleted
        u?.type !== 2 && // disabled
        fullName.length > 0 &&
        (mentionQuery === "" || fullName.includes(mentionQuery.toLowerCase()))
      );
    })
    .sort((a, b) =>
      getFullName(a).localeCompare(getFullName(b), undefined, {
        sensitivity: "base",
      }),
    );

  const selectMention = useCallback(
    (userObj) => {
      const fullName = getFullName(userObj);
      const before = text.slice(0, cursorPosition);
      const after = text.slice(cursorPosition);
      const newBefore = before.replace(/@([\w\s]*)$/, `@${fullName} `);
      const newText = newBefore + after;
      setText(newText);
      setShowMentions(false);
      // Restore caret position right after the inserted mention.
      requestAnimationFrame(() => {
        const field = fieldRef?.current;
        if (field) {
          const pos = newBefore.length;
          field.focus();
          field.setSelectionRange?.(pos, pos);
          setCursorPosition(pos);
        }
      });
    },
    [text, cursorPosition, setText, fieldRef],
  );

  // Close the dropdown — call this on blur (with a small delay so a click
  // on the dropdown registers first).
  const closeDropdown = useCallback(() => setShowMentions(false), []);

  return {
    showMentions,
    candidates,
    selectMention,
    closeDropdown,
    fieldHandlers: { onChange },
  };
}

// Tokenize text into plain strings and { mention: user } objects for rendering.
// Returns an array of either strings (plain text) or { mention: userObject }.
// Used by PostCard / CommentCard to render @mentions as styled spans.
export function renderWithMentions(text, users) {
  if (!text) return [];
  const tokens = [];
  let remaining = text;
  // Build a regex that matches any known user's full name preceded by @.
  // Sort by length descending so "@Jane Smith" wins over "@Jane".
  const sortedUsers = [...(users || [])]
    .filter((u) => getFullName(u))
    .sort((a, b) => getFullName(b).length - getFullName(a).length);

  outer: while (remaining.length > 0) {
    const atIdx = remaining.indexOf("@");
    if (atIdx === -1) {
      tokens.push(remaining);
      break;
    }
    if (atIdx > 0) tokens.push(remaining.slice(0, atIdx));
    const rest = remaining.slice(atIdx);
    for (const u of sortedUsers) {
      const name = getFullName(u);
      if (
        rest.startsWith(`@${name}`) &&
        !/[\w]/.test(rest.charAt(name.length + 1) || "")
      ) {
        tokens.push({ mention: u });
        remaining = rest.slice(name.length + 1);
        continue outer;
      }
    }
    // No user matched — treat the @ as plain text and continue past it.
    tokens.push("@");
    remaining = rest.slice(1);
  }
  return tokens;
}
